import { defineStore } from 'pinia';
import type { ProductCategoriesQuery, NavigationTeasersQuery } from '~/lib/goliath/goliath.types';
import { getOffersByGroup, type Promotion } from '~/lib/offers-by-group';
import { captureMessage } from '~/lib/sentry';

export type NavigationTeaser = Promotion<{
  label?: string;
  heading: string;
  subheading?: string;
  alt?: string;
  customClass?: string;
  image: string;
  imagePosition?: string;
  link: {
    label: string;
    path?: string;
    theme?: string;
  };
  noPrice?: boolean;
  theme?: string;
  tileGroups: string[];
}>;

export type NavigationTeaserOffer = NonNullable<
  NonNullable<NonNullable<NavigationTeasersQuery['offers']>['entries']>[number]
>;

export type NavigationTeaserWithOffer = {
  offer?: NavigationTeaserOffer | undefined;
} & NavigationTeaser;

export type ListItem = {
  name: string;
  path: string;
};

export type Category = ListItem & {
  // this appears under "Kategorien"
  children?: ListItem[];
  mappingPath?: string; // used, when `path` differs from the category name in Goliath.
  // this appears under "Inhalte"
  listItems?: ListItem[];
  buttonLabel?: string;
  teasers?: NavigationTeaserWithOffer[];
};

export interface NavigationState {
  items: Category[];
}

export type GoliathCategory = NonNullable<
  NonNullable<ProductCategoriesQuery['productCategories']>['entries']
>[number];

export const TEASER_DATA: NavigationTeaser[] = [
  {
    articleNumber: '99935421',
    label: 'Telekom T Phone 2',
    heading: 'Einsteigerphone<br/>zum Spitzenpreis',
    image:
      '/content/content-images/navigation-teasers/441527d1af2dc04819deb98d1e364452/teaser-t-phone-2-bg@2x.jpg',
    alt: 'Telekom T Phone 2.',
    link: {
      label: 'Zum T Phone 2',
    },
    theme: 'light',
    tileGroups: ['smartphones-und-handys/smartphones'],
  },
  {
    articleNumber: '99934489',
    label: 'Fairphone 5',
    heading: 'Das nachhaltige Smartphone',
    image:
      '/content/content-images/navigation-teasers/5308d8134f05d96695f9855b91f75777/teaser-fairphone-bg@2x.jpg',
    alt: 'Fairphone 5.',
    link: {
      label: 'Zum Fairphone',
    },
    tileGroups: ['smartphones-und-handys/smartphones'],
  },
  {
    articleNumber: '99935296',
    label: 'Apple 13" iPad Air (2024) Wi-Fi + 5G',
    heading: 'Mit Liquid Retina Display',
    image:
      '/content/content-images/navigation-teasers/faeee6e21c10057b1b56cb13d43e9a5a/teaser-ipad@2x.jpg',
    alt: 'Apple 13" iPad Air.',
    link: {
      label: 'Zum Angebot',
    },
    tileGroups: ['tablets-und-zubehoer'],
  },
  {
    articleNumber: '0840244600082',
    label: 'Ein kompaktes Gerät mit vielen Funktionen',
    heading: 'Smartwatch<br/>Google Pixel<br/>Watch WiFi',
    image:
      '/content/content-images/navigation-teasers/73466aafa028683b91a1c0b73123085d/google-pixel-watch@2x.jpg',
    alt: 'Google Pixel Watch.',
    link: {
      label: 'Zum Angebot',
    },
    theme: 'light',
    tileGroups: ['wearables'],
  },
  {
    articleNumber: '6941812775790',
    label: 'XIAOMI Smart Band 9',
    heading: 'Dein Stil, Dein Tempo',
    image:
      '/content/content-images/navigation-teasers/1eec59536ef94f49f16dcb3701fac9e0/teaser-smart-band-9-bg@2x.jpg',
    alt: 'XIAOMI Smart Band 9.',
    link: {
      label: 'Zum Angebot',
    },
    tileGroups: ['wearables'],
  },
  {
    label: 'Smarte Gerätevielfalt',
    heading: 'Für Mehr Komfort mit einem smarten Zuhause',
    image:
      '/content/content-images/navigation-teasers/ea369797b63bf01e2221f405df608faf/teaser-smart-home@2x.jpg',
    alt: 'Verschiedene Smart Home Geräte: Wiz LED-Lampe, Wiz Smart Plug, Eurotronic Thermostat.',
    link: {
      label: 'Alle Geräte im Überblick',
      path: '/smart-home',
      theme: 'outline',
    },
    tileGroups: ['smart-home'],
  },
  {
    articleNumber: '0194644098018OT',
    label: 'Anker 3-in-1 Cube with MagSafe',
    heading: 'Der Multi-device&shy;schnelllader',
    image:
      '/content/content-images/navigation-teasers/8fdc12d56d65dab4cb500ced2901bf36/teaser-anker-cube@2x.jpg',
    alt: 'Anker 3-in-1 Cube with MagSafe.',
    link: {
      label: 'Zum Angebot',
    },
    tileGroups: ['zubehoer'],
  },
  {
    articleNumber: '6934177780554',
    label: 'XIAOMI Smart Blender',
    heading: 'Frische<br/>Smoothies Auf<br/>Knopfdruck',
    image:
      '/content/content-images/navigation-teasers/4fd65749a730cfc950eaf75ed449c4a4/teaser-xiaomi-blender@2x.jpg',
    alt: 'Ein XIAOMI Smart Blender voll mit Orangen, Erdbeeren und Melone.',
    link: {
      label: 'Zum Angebot',
    },
    tileGroups: ['zubehoer'],
  },
  {
    theme: 'light',
    heading:
      "<span><span class='text--primary'>MAGENTA</span> <span>BLACK</span> <br><span class='text--primary'>DAYS</span> – 20&nbsp;€ Start&shy;Guthaben",
    label: 'Zahl einfach per Handyrechnung',
    image:
      '/content/content-images/dcb/november/8cf2fc06261284dd2e8cb0d5068c13b7/teaser-aktionen-mdb.png',
    alt: 'Ein E-Sooter, Amazon Gutschein und Gasgrill.',
    link: {
      label: 'Jetzt Gutschrift sichern',
      path: '/handyrechnung-aktion',
    },
    tileGroups: ['preisvorteile-sichern'],
  },
];

export const useNavigationStore = defineStore('navigation', {
  state: (): NavigationState => ({
    /**
     * We have to remove entries, sort them and add custom content to the goliath data.
     * We switch the logic. This Collection will be extended with the children from goliath.
     */
    items: [
      {
        name: 'Smartphones',
        path: 'smartphones-und-handys/smartphones',
        mappingPath: 'smartphones-und-handys',
        children: [],
      },
      {
        name: 'Tablets',
        path: 'tablets-und-zubehoer/tablets',
        mappingPath: 'tablets-und-zubehoer',
        children: [],
      },
      {
        name: 'Wearables',
        path: 'wearables',
        children: [],
      },
      {
        name: 'Smart Home',
        path: 'smart-home',
        children: [],
      },
      {
        name: 'TV',
        path: 'tv/fernseher',
        mappingPath: 'tv',
        children: [],
      },
      {
        name: 'Zubehör',
        path: 'zubehoer',
        children: [
          {
            name: 'Apple Original Zubehör',
            path: 'zubehoer/_apple', // In Goliath the path does NOT start with a slash and it will be added!
          },
        ],
      },
      {
        name: 'Themenwelten',
        path: 'themenwelten',
        listItems: [
          {
            name: 'Smart Home',
            path: 'smart-home-entdecken',
          },
          {
            name: 'agood company',
            path: 'a-good-company',
          },
          {
            name: 'Xiaomi',
            path: 'xiaomi',
          },
          {
            name: 'PanzerGlass',
            path: 'panzerglass',
          },
          // {
          //   name: 'Google',
          //   path: 'google',
          // },
          {
            name: 'Samsung',
            path: 'samsung',
          },
          {
            name: 'Anker',
            path: 'anker',
          },
          {
            name: 'soundcore',
            path: 'soundcore',
          },
        ],
      },
      {
        name: 'Aktionen',
        path: 'preisvorteile-sichern',
        children: [],
        listItems: [
          {
            name: 'Alle laufenden Aktionen',
            path: 'preisvorteile-sichern',
          },
          {
            name: 'Reduziertes Zubehör',
            path: 'zubehoer?discounted=true',
          },
          {
            name: 'Zahl einfach per Handyrechnung',
            path: 'zahlung-per-handyrechnung',
          },
        ],
      },
    ],
  }),

  getters: {
    navigationItems(state) {
      return state.items;
    },
  },

  actions: {
    setListChildren(params: { path: string; listItems: ListItem[] }) {
      const navigationItem = this.items.find(
        (item: Category) => item.mappingPath === params.path || item.path === params.path
      );
      if (navigationItem?.children) {
        if (navigationItem.children.length > 1) {
          captureMessage('Navigation item not empty', {
            contexts: {
              params,
              navigationItem,
            },
          });
        }
        params.listItems.forEach((i) => {
          navigationItem.children?.push(i);
        });
      }
    },

    setTeaserOffers(teaserOffers: NavigationTeaserWithOffer[]) {
      this.items.forEach((item: Category) => {
        // eslint-disable-next-line no-param-reassign
        item.teasers = getOffersByGroup(teaserOffers, item.path, 2);
      });
    },

    setListItems(params: { path: string; listItems: ListItem[] }) {
      const navigationItem = this.items.find((item: Category) => item.path === params.path);
      if (navigationItem) navigationItem.listItems = params.listItems;
    },
  },
});
